// Phase-25 — Canonical Category Registry admin page.
//
// Surface: /admin/categories
// Purpose: let admins SEE all categories in the workspace, their rule usage
//          across the card library, RENAME (with rule side-effects), MERGE
//          duplicates into a single canonical entry, and ADOPT orphans
//          (category values found in rules but missing from the registry).
//
// Data sources
// ============
//  • AdminAPI.categories.list({ includeInactive: true })  — registry rows
//  • AdminAPI.categories.usage()                          — rule/card counts
//  • AdminAPI.categories.orphans()                        — un-canonical names
//
// All mutations route through services/category_service on the backend so
// rule documents in cards_library are kept in sync automatically.
import { useEffect, useMemo, useState } from 'react';
import {
  Plus, Search, Pencil, Trash2, Loader2, AlertTriangle,
  Tags, Merge, GitBranch, CheckCircle2, RefreshCw, Eraser,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AdminAPI } from '@/lib/admin-api';
import { invalidateCategories } from '@/hooks/useCategories';
import { toast } from 'sonner';

const EMPTY_FORM = {
  value: '', label: '', description: '',
  aliases: '', icon: '', sort_order: 100, is_active: true,
};

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [usage, setUsage] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [includeInactive, setIncludeInactive] = useState(true);
  const [tab, setTab] = useState('registry');

  // Create/edit drawer state
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Rename dialog state
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Merge dialog state
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTarget, setMergeTarget] = useState('');
  const [mergeAddAlias, setMergeAddAlias] = useState(true);
  const [merging, setMerging] = useState(false);

  // Delete dialog state
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Adopt-orphan dialog state (lifted into the same merge flow)
  const [adoptingOrphan, setAdoptingOrphan] = useState(null);

  // Strip-orphan dialog state (destructive — removes from all rules)
  const [stripOrphanTarget, setStripOrphanTarget] = useState(null);
  const [stripping, setStripping] = useState(false);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const [cats, usg, orph] = await Promise.all([
        AdminAPI.categories.list({ includeInactive }),
        AdminAPI.categories.usage(),
        AdminAPI.categories.orphans(),
      ]);
      setCategories(cats || []);
      setUsage((usg && usg.usage) || []);
      setOrphans((orph && orph.orphans) || []);
      // Phase-25 fix — every admin reload also invalidates the shared
      // consumer-facing cache used by Rule Builder, Recommend, Simulator.
      // Without this, a delete / rename / merge here would not be visible
      // to consumers until they hard-refreshed the page.
      invalidateCategories();
    } catch (e) {
      setError(e.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  useEffect(() => { reload(); }, [includeInactive]);

  // Build a lookup of {value → usage row} so we can show rule_count next to
  // each canonical row without a second round-trip.
  const usageByValue = useMemo(() => {
    const m = new Map();
    for (const u of usage) m.set(u.value, u);
    return m;
  }, [usage]);

  const filtered = useMemo(() => {
    if (!q) return categories;
    const qq = q.toLowerCase();
    return categories.filter((c) =>
      `${c.value} ${c.label} ${(c.aliases || []).join(' ')}`.toLowerCase().includes(qq),
    );
  }, [categories, q]);

  // ─── Create / Edit ─────────────────────────────────────────────────
  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      value: c.value || '',
      label: c.label || '',
      description: c.description || '',
      aliases: (c.aliases || []).join(', '),
      icon: c.icon || '',
      sort_order: c.sort_order ?? 100,
      is_active: c.is_active !== false,
    });
    setOpen(true);
  };
  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.value && !editing) {
      toast.error('Value is required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        label: form.label || form.value || editing?.value,
        description: form.description || null,
        aliases: form.aliases.split(',').map((s) => s.trim()).filter(Boolean),
        icon: form.icon || null,
        sort_order: Number.isFinite(Number(form.sort_order)) ? Number(form.sort_order) : 100,
        is_active: !!form.is_active,
      };
      if (editing) {
        await AdminAPI.categories.update(editing.id, body);
        toast.success(`Category '${editing.value}' updated`);
      } else {
        await AdminAPI.categories.create({ value: form.value, ...body });
        toast.success(`Category '${form.value}' created`);
      }
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ─── Rename ─────────────────────────────────────────────────────────
  const submitRename = async () => {
    if (!renameTarget || !renameValue || renameValue === renameTarget.value) {
      setRenameTarget(null);
      return;
    }
    setRenaming(true);
    try {
      await AdminAPI.categories.rename(renameTarget.id, renameValue);
      toast.success(`Renamed '${renameTarget.value}' → '${renameValue}' (rules updated)`);
      setRenameTarget(null);
      setRenameValue('');
      reload();
    } catch (err) {
      // If 409 (target value exists), guide admin to merge instead
      if (/already exists/i.test(err.message || '')) {
        toast.error('That name already exists — use Merge instead.', { duration: 5000 });
      } else {
        toast.error(err.message || 'Rename failed');
      }
    } finally {
      setRenaming(false);
    }
  };

  // ─── Merge ──────────────────────────────────────────────────────────
  // Two distinct flows under one button:
  //   • source is canonical (has id)   → POST /admin/categories/{id}/merge
  //   • source is an orphan (no id)    → POST /admin/categories/merge-orphan
  // The orphan path REWRITES every rule referencing `source_value` to use
  // `target_value`, then (optionally) adds the orphan name as an alias on
  // the target. After this completes, the orphan disappears from the
  // orphan list (because no rule references it anymore).
  const submitMerge = async () => {
    const source = mergeSource;
    const target = (mergeTarget || '').trim();
    if (!source || !target || target === source.value) {
      setMergeSource(null);
      return;
    }
    setMerging(true);
    try {
      let result;
      if (source.id) {
        result = await AdminAPI.categories.merge(source.id, target, mergeAddAlias);
        toast.success(`Merged '${source.value}' → '${target}' (${result.rules_touched || 0} rule${result.rules_touched === 1 ? '' : 's'} rewritten, source deactivated)`);
      } else {
        // Orphan path — verify target exists in cache first for early UX feedback.
        const tgt = categories.find((c) => c.value === target && c.is_active !== false);
        if (!tgt) {
          toast.error(`Target '${target}' is not an active canonical category. Create it first, then merge.`);
          return;
        }
        result = await AdminAPI.categories.mergeOrphan(source.value, target, mergeAddAlias);
        toast.success(`Adopted '${source.value}' into '${target}' (${result.rules_touched || 0} rule${result.rules_touched === 1 ? '' : 's'} rewritten${mergeAddAlias ? ', alias added' : ''})`);
      }
      setMergeSource(null);
      setMergeTarget('');
      setMergeAddAlias(true);
      setAdoptingOrphan(null);
      reload();
    } catch (err) {
      toast.error(err.message || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  // ─── Strip orphan (DESTRUCTIVE) ─────────────────────────────────────
  // Removes the value from every rule's match.categories[] without
  // remapping to anything. Use when the orphan is genuine junk (MCC
  // codes leaked in, brand names that shouldn't be categories, etc.).
  const submitStripOrphan = async () => {
    if (!stripOrphanTarget) return;
    setStripping(true);
    try {
      const result = await AdminAPI.categories.stripOrphan(stripOrphanTarget.value);
      toast.success(
        `Stripped '${stripOrphanTarget.value}' from ${result.rules_touched || 0} rule${result.rules_touched === 1 ? '' : 's'} across ${result.cards_touched || 0} card${result.cards_touched === 1 ? '' : 's'}`,
      );
      setStripOrphanTarget(null);
      reload();
    } catch (err) {
      toast.error(err.message || 'Strip failed');
    } finally {
      setStripping(false);
    }
  };

  // ─── Delete ─────────────────────────────────────────────────────────
  const submitDelete = async (hard = false) => {
    if (!deleteTarget) return;
    try {
      await AdminAPI.categories.remove(deleteTarget.id, { hard });
      toast.success(hard ? 'Category permanently deleted' : 'Category deactivated');
      setDeleteTarget(null);
      reload();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  // ────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="admin-categories-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Tags className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-semibold tracking-tight" data-testid="admin-categories-title">Categories</h1>
          <Badge variant="outline" className="ml-2" data-testid="admin-categories-count">
            {categories.length} canonical · {orphans.length} orphan{orphans.length === 1 ? '' : 's'}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={reload}
            disabled={loading}
            data-testid="admin-categories-reload"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="ml-1.5">Reload</span>
          </Button>
          <Button onClick={openCreate} data-testid="admin-categories-create">
            <Plus className="h-4 w-4" /> <span className="ml-1.5">New category</span>
          </Button>
        </div>
      </div>

      {/* Orphan warning banner — only shown when orphans exist. */}
      {orphans.length > 0 && (
        <Card className="border-yellow-500/40 bg-yellow-500/5" data-testid="admin-categories-orphans-banner">
          <div className="flex items-start gap-3 p-4">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-sm">
                {orphans.length} non-canonical {orphans.length === 1 ? 'category' : 'categories'} found in rules
              </p>
              <p className="text-sm text-muted-foreground mt-0.5">
                These values appear in card rules but aren&apos;t in the canonical registry. Use the <span className="font-medium">Orphans</span> tab below to merge or adopt them.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setTab('orphans')} data-testid="admin-categories-jump-orphans">
              Review orphans
            </Button>
          </div>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList>
          <TabsTrigger value="registry" data-testid="admin-categories-tab-registry">
            Registry ({categories.length})
          </TabsTrigger>
          <TabsTrigger value="orphans" data-testid="admin-categories-tab-orphans">
            Orphans ({orphans.length})
          </TabsTrigger>
        </TabsList>

        {/* ─── REGISTRY TAB ────────────────────────────────────────── */}
        <TabsContent value="registry" className="mt-3">
          <Card>
            <div className="p-3 border-b border-border flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search name, label, alias…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-8 h-9"
                  data-testid="admin-categories-search"
                />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Switch
                  id="ic-toggle"
                  checked={includeInactive}
                  onCheckedChange={setIncludeInactive}
                  data-testid="admin-categories-include-inactive"
                />
                <Label htmlFor="ic-toggle" className="cursor-pointer">Include inactive</Label>
              </div>
            </div>

            {error && (
              <div className="p-4 text-sm text-destructive flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> {error}
              </div>
            )}

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Value</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Aliases</TableHead>
                  <TableHead className="text-right w-[110px]">Usage</TableHead>
                  <TableHead className="w-[80px] text-center">Status</TableHead>
                  <TableHead className="w-[160px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell>
                  </TableRow>
                ))}
                {!loading && filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      {q ? 'No matches.' : 'No categories yet — click New category to add one.'}
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filtered.map((c) => {
                  const u = usageByValue.get(c.value);
                  return (
                    <TableRow key={c.id} data-testid={`admin-categories-row-${c.value}`}>
                      <TableCell className="font-medium">{c.value}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{c.label}</TableCell>
                      <TableCell className="text-xs">
                        {(c.aliases && c.aliases.length > 0)
                          ? <div className="flex flex-wrap gap-1">
                              {c.aliases.slice(0, 4).map((a) => (
                                <Badge variant="outline" key={a} className="text-[10px] font-normal">{a}</Badge>
                              ))}
                              {c.aliases.length > 4 && (
                                <Badge variant="outline" className="text-[10px] font-normal">+{c.aliases.length - 4}</Badge>
                              )}
                            </div>
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {u ? (
                          <span className="text-sm tabular-nums" title={`${u.rule_count} rules across ${u.card_count} cards`}>
                            {u.rule_count} rules · {u.card_count} cards
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">unused</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {c.is_active
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" />
                          : <Badge variant="secondary" className="text-[10px]">inactive</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => openEdit(c)}
                            data-testid={`admin-categories-edit-${c.value}`}
                          ><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => { setRenameTarget(c); setRenameValue(c.value); }}
                            data-testid={`admin-categories-rename-${c.value}`}
                            title="Rename (rewrites all rules)"
                          ><GitBranch className="h-3.5 w-3.5" /></Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => { setMergeSource(c); setMergeTarget(''); setMergeAddAlias(true); }}
                            data-testid={`admin-categories-merge-${c.value}`}
                            title="Merge into another (rewrites all rules)"
                          ><Merge className="h-3.5 w-3.5" /></Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => setDeleteTarget(c)}
                            data-testid={`admin-categories-delete-${c.value}`}
                          ><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ─── ORPHANS TAB ─────────────────────────────────────────── */}
        <TabsContent value="orphans" className="mt-3">
          <Card>
            <div className="p-3 border-b border-border text-sm text-muted-foreground">
              These category values appear in <span className="font-medium text-foreground">card rules</span> but
              not in the canonical registry. Choose <span className="font-medium text-foreground">Merge</span> to
              repoint affected rules to a canonical target (and optionally add as alias), or
              <span className="font-medium text-destructive"> Strip</span> to remove the value entirely from every
              rule that references it.
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Orphan value</TableHead>
                  <TableHead className="text-right w-[90px]">Usage</TableHead>
                  <TableHead className="w-[300px]">Used by</TableHead>
                  <TableHead>Suggested target</TableHead>
                  <TableHead className="w-[260px] text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={5}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
                )}
                {!loading && orphans.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      <div className="flex items-center gap-2 justify-center">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        No orphans — every rule references a canonical category.
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && orphans.map((o) => {
                  const cardNames = o.card_names || [];
                  const cardBanks = o.card_banks || [];
                  // Combine bank + name into 'Bank · Name' chips when both exist;
                  // fall back to either alone.
                  const cardLabels = cardNames.length > 0
                    ? cardNames
                    : cardBanks;
                  return (
                    <TableRow key={`orphan-${o.value}`} data-testid={`admin-categories-orphan-row-${o.value}`}>
                      <TableCell className="font-medium align-top pt-3">{o.value}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm align-top pt-3">
                        {o.rule_count} rule{o.rule_count === 1 ? '' : 's'}
                        <div className="text-xs text-muted-foreground">
                          {o.card_count} card{o.card_count === 1 ? '' : 's'}
                        </div>
                      </TableCell>
                      <TableCell className="align-top pt-3">
                        {cardLabels.length > 0 ? (
                          <div className="flex flex-wrap gap-1" data-testid={`admin-categories-orphan-cards-${o.value}`}>
                            {cardLabels.slice(0, 5).map((name, i) => (
                              <Badge variant="outline" key={`${o.value}-card-${i}`} className="text-[10px] font-normal">
                                {name}
                              </Badge>
                            ))}
                            {cardLabels.length > 5 && (
                              <Badge variant="outline" className="text-[10px] font-normal" title={cardLabels.slice(5).join(', ')}>
                                +{cardLabels.length - 5} more
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="align-top pt-3">
                        {o.suggested_target ? (
                          <Badge variant="secondary" className="font-normal">
                            {o.suggested_target}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">no match — choose target or strip</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right align-top pt-2">
                        <div className="inline-flex gap-1.5 flex-wrap justify-end">
                          {o.suggested_target && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setAdoptingOrphan(o);
                                setMergeSource({ value: o.value, id: null });
                                setMergeTarget(o.suggested_target);
                                setMergeAddAlias(true);
                              }}
                              data-testid={`admin-categories-adopt-orphan-${o.value}`}
                              title={`Merge into ${o.suggested_target} (rewrites rules + adds alias)`}
                            >
                              <Merge className="h-3.5 w-3.5 mr-1" />
                              Merge → {o.suggested_target}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setMergeSource({ value: o.value, id: null });
                              setMergeTarget('');
                              setMergeAddAlias(true);
                            }}
                            data-testid={`admin-categories-merge-orphan-${o.value}`}
                            title="Pick a different canonical target"
                          >
                            Choose…
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => setStripOrphanTarget(o)}
                            data-testid={`admin-categories-strip-orphan-${o.value}`}
                            title="Remove this value from all rules (destructive)"
                          >
                            <Eraser className="h-3.5 w-3.5 mr-1" />
                            Strip
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Create/Edit drawer ─────────────────────────────────────────── */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="admin-categories-drawer">
          <SheetHeader>
            <SheetTitle>{editing ? `Edit '${editing.value}'` : 'New category'}</SheetTitle>
            <SheetDescription>
              {editing
                ? 'Edit display label, description, aliases, sort, or active state. Use the Rename action on the row to change the canonical value.'
                : 'The value is Title-Cased + acronym-normalised on save (e.g. "gst" → "GST").'}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={onSubmit} className="space-y-4 mt-4">
            {!editing && (
              <div className="space-y-1.5">
                <Label htmlFor="cat-value">Value (canonical) *</Label>
                <Input
                  id="cat-value" value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  placeholder="e.g. Travel"
                  data-testid="admin-categories-form-value"
                  required
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="cat-label">Label</Label>
              <Input
                id="cat-label" value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder={editing ? editing.value : 'Display label (defaults to value)'}
                data-testid="admin-categories-form-label"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cat-desc">Description</Label>
              <Textarea
                id="cat-desc" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional one-liner shown in tooltips and Rule Builder hints"
                rows={2}
                data-testid="admin-categories-form-desc"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cat-aliases">Aliases (comma-separated)</Label>
              <Input
                id="cat-aliases" value={form.aliases}
                onChange={(e) => setForm({ ...form, aliases: e.target.value })}
                placeholder="e.g. Groceries, Supermarket"
                data-testid="admin-categories-form-aliases"
              />
              <p className="text-xs text-muted-foreground">
                Past typos / variants that should normalise to this canonical value on Rule writes.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="cat-sort">Sort order</Label>
                <Input
                  id="cat-sort" type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  data-testid="admin-categories-form-sort"
                />
              </div>
              <div className="space-y-1.5 flex flex-col">
                <Label htmlFor="cat-active">Active</Label>
                <div className="flex-1 flex items-center">
                  <Switch
                    id="cat-active"
                    checked={form.is_active}
                    onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                    data-testid="admin-categories-form-active"
                  />
                </div>
              </div>
            </div>
            <SheetFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="admin-categories-form-submit">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
                {editing ? 'Save changes' : 'Create category'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* ── Rename dialog ─────────────────────────────────────────────── */}
      <AlertDialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <AlertDialogContent data-testid="admin-categories-rename-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rename category</AlertDialogTitle>
            <AlertDialogDescription>
              Renames <span className="font-semibold">&apos;{renameTarget?.value}&apos;</span> across every rule that references it.
              This is a transactional rewrite of card rules in the library — usage counts will refresh after save.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5 mt-2">
            <Label htmlFor="rename-input">New value</Label>
            <Input
              id="rename-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              data-testid="admin-categories-rename-input"
            />
            <p className="text-xs text-muted-foreground">
              Will be Title-Cased + acronym-normalised on save (e.g. &quot;gst&quot; → &quot;GST&quot;).
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={renaming}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitRename}
              disabled={renaming || !renameValue || renameValue === renameTarget?.value}
              data-testid="admin-categories-rename-submit"
            >
              {renaming && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              Rename + rewrite rules
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Merge dialog ──────────────────────────────────────────────── */}
      <AlertDialog open={!!mergeSource} onOpenChange={(o) => !o && setMergeSource(null)}>
        <AlertDialogContent data-testid="admin-categories-merge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {adoptingOrphan ? `Adopt orphan '${mergeSource?.value}'` : `Merge '${mergeSource?.value}'`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {adoptingOrphan
                ? `Add '${mergeSource?.value}' as an alias on a canonical category. Existing rules using the orphan name will normalise on their next save; new writes will auto-resolve.`
                : `Repoint every rule referencing '${mergeSource?.value}' to a different canonical category. The source category will be soft-deactivated.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1.5">
              <Label>Merge into</Label>
              <Select value={mergeTarget} onValueChange={setMergeTarget}>
                <SelectTrigger data-testid="admin-categories-merge-target">
                  <SelectValue placeholder="Pick a canonical category…" />
                </SelectTrigger>
                <SelectContent>
                  {categories
                    .filter((c) => c.value !== mergeSource?.value && c.is_active !== false)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.value}>
                        {c.value} {usageByValue.get(c.value)?.rule_count ? `(${usageByValue.get(c.value).rule_count} rules)` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="merge-alias"
                checked={mergeAddAlias}
                onCheckedChange={setMergeAddAlias}
                data-testid="admin-categories-merge-alias"
              />
              <Label htmlFor="merge-alias" className="cursor-pointer">
                Add &apos;{mergeSource?.value}&apos; as an alias on the target (recommended)
              </Label>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={merging}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitMerge}
              disabled={merging || !mergeTarget || mergeTarget === mergeSource?.value}
              data-testid="admin-categories-merge-submit"
            >
              {merging && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              {adoptingOrphan ? 'Adopt as alias' : 'Merge + rewrite rules'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Strip-orphan dialog (DESTRUCTIVE) ─────────────────────────── */}
      <AlertDialog open={!!stripOrphanTarget} onOpenChange={(o) => !o && setStripOrphanTarget(null)}>
        <AlertDialogContent data-testid="admin-categories-strip-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Eraser className="h-4 w-4" />
              Strip &apos;{stripOrphanTarget?.value}&apos; from all rules?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the value <span className="font-mono font-medium">{stripOrphanTarget?.value}</span> from
              every rule&apos;s <code>match.categories[]</code> array — affecting
              <span className="font-medium"> {stripOrphanTarget?.rule_count} rule{stripOrphanTarget?.rule_count === 1 ? '' : 's'} </span>
              across <span className="font-medium">{stripOrphanTarget?.card_count} card{stripOrphanTarget?.card_count === 1 ? '' : 's'}</span>.
              Other categories on the affected rules are preserved. <span className="text-destructive font-medium">This is destructive
              and cannot be undone</span> — only proceed if the value is genuine junk (e.g. an MCC code or brand string that
              shouldn&apos;t be a category).
            </AlertDialogDescription>
          </AlertDialogHeader>
          {(stripOrphanTarget?.card_names || []).length > 0 && (
            <div className="border border-border rounded-md p-3 bg-muted/30 text-xs space-y-1.5 max-h-32 overflow-y-auto">
              <div className="text-muted-foreground font-medium uppercase tracking-wide text-[10px]">
                Cards that will be modified
              </div>
              <div className="flex flex-wrap gap-1">
                {(stripOrphanTarget.card_names || []).map((n, i) => (
                  <Badge key={`stripcard-${i}`} variant="outline" className="text-[10px] font-normal">{n}</Badge>
                ))}
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={stripping}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitStripOrphan}
              disabled={stripping}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="admin-categories-strip-submit"
            >
              {stripping && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              Strip from {stripOrphanTarget?.rule_count} rule{stripOrphanTarget?.rule_count === 1 ? '' : 's'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Delete dialog ─────────────────────────────────────────────── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="admin-categories-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &apos;{deleteTarget?.value}&apos;?</AlertDialogTitle>
            <AlertDialogDescription>
              By default this <span className="font-medium">soft-deletes</span> the category (sets <code>is_active=false</code>),
              so historical references aren&apos;t broken. Use the hard delete only when you&apos;re sure no rules reference it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button variant="outline" onClick={() => submitDelete(false)} data-testid="admin-categories-delete-soft">
              Soft-delete (deactivate)
            </Button>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => submitDelete(true)}
              data-testid="admin-categories-delete-hard"
            >
              Hard delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
